import { useDispatchAlert } from "../ui/Alert";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { ReleaseNotesQueryNames } from "../../api/Queries";
import {
    addReleaseNote,
    deleteReleaseNote,
    getExistingReleaseNotes,
    updateReleaseNote
} from "../../api/ReleaseNotes/ReleaseNotesService";
import React, { useCallback } from "react";
import PagePaper from "../ui/PagePaper";
import { Divider } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReleaseNoteModel } from "../../types/ReleaseNoteModel";
import ReleaseNoteEditor from "./ReleaseNoteEditor";
import {AxiosResponse} from "axios";
import {ReleaseNotesDataGrid} from "./ReleaseNotesDataGrid";

const useStyles = makeStyles((theme) => ({
    divider: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    button: {
        marginTop: theme.spacing(1),
    },
    margin: {
        marginBottom: theme.spacing(2),
    },
    infoPage: {
        paddingTop: theme.spacing(5),
    },
    infoPaper: {
        padding: theme.spacing(5),
    },
}));

export default function AddReleaseNoteContainer() {
    // Set theme for MDEditor
    document.documentElement.dataset.colorMode = 'light';
    
    const classes = useStyles();
    const dispatchAlert = useDispatchAlert();
    const queryClient = useQueryClient();
    
    const [ editReleaseNote, setEditReleaseNote ] = React.useState<ReleaseNoteModel>({
        id: undefined,
        version: "",
        title: "",
        message: "",
        timestampUtc: new Date(),
    });
    const [ releaseNotes, setReleaseNotes ] = React.useState<ReleaseNoteModel[] | undefined>(undefined);
    const existingReleaseNotesQuery = useQuery(
        ReleaseNotesQueryNames.GetExistingReleaseNotes,
        () => getExistingReleaseNotes(),
        {
            onError: () => {
                dispatchAlert({
                    message:
                        "Something went wrong while retrieving the existing release notes",
                    messageType: "error",
                });
            },
            onSuccess: (response) => {
                setReleaseNotes(response.data);
            }
        }
    )
    
    const executeAddReleaseNote = useMutation(
        async (releaseNote: ReleaseNoteModel) : Promise<AxiosResponse> => {
            return addReleaseNote(releaseNote);
        },
        {
            onError: () => {
                dispatchAlert({
                    message: "Something went wrong while saving the release note",
                    messageType: "error",
                });
            },
            onSuccess: async () => {
                dispatchAlert({
                    message: "Successfully added the release note",
                    messageType: "success",
                });
                await queryClient.invalidateQueries({
                    queryKey: [ReleaseNotesQueryNames.GetExistingReleaseNotes]
                });
            },
        }
    );
    
    const executeDeleteReleaseNote = useMutation(
        async (releaseNote: ReleaseNoteModel) : Promise<AxiosResponse> => {
            return deleteReleaseNote(releaseNote.id!);
        }, {
            onError: () => {
                dispatchAlert({
                    message: "Something went wrong while deleting the release note",
                    messageType: "error",
                });
            },
            onSuccess: async () => {
                dispatchAlert({
                    message: "Successfully deleted the release note",
                    messageType: "success",
                });
                await queryClient.invalidateQueries({
                    queryKey: [ReleaseNotesQueryNames.GetExistingReleaseNotes]
                })
            }
        }
    )
    
    const executeUpdateReleaseNote = useMutation(
        async (releaseNote: ReleaseNoteModel) : Promise<AxiosResponse> => {
            if (releaseNote.id === undefined) {
                throw new Error("Cannot update a release note without an id");
            }
            return updateReleaseNote(releaseNote.id, releaseNote);
        }, {
            onError: () => {
                dispatchAlert({
                    message: "Something went wrong while updating the release note",
                    messageType: "error",
                });
            },
            onSuccess: async () => {
                dispatchAlert({
                    message: "Successfully updated the release note",
                    messageType: "success",
                });
                await queryClient.invalidateQueries({
                    queryKey: [ReleaseNotesQueryNames.GetExistingReleaseNotes]
                })
            }
        }
    )

    const handleOnSubmit = useCallback(async (updatedReleaseNote : ReleaseNoteModel) : Promise<boolean> => {
        if (updatedReleaseNote.id === undefined) {
            return (await executeAddReleaseNote.mutateAsync(updatedReleaseNote)).status === 200;    
        } else {
            return (await executeUpdateReleaseNote.mutateAsync(updatedReleaseNote)).status === 200;    
        }
    }, []);
    
    const editReleaseNoteClickHandler = (releaseNote: ReleaseNoteModel) => {
        setEditReleaseNote(releaseNote);
    }
    
    const deleteReleaseNoteClickHandler = (releaseNote: ReleaseNoteModel) => {
        executeDeleteReleaseNote.mutate(releaseNote) 
    }
    
    const resetButtonClickHandler = () => {
        setEditReleaseNote({
            id: undefined,
            version: "",
            title: "",
            message: "",
            timestampUtc: new Date(),
        });
    }

    return (
        <div className={classes.margin}>
            <PagePaper
                isLoading={                 
                    executeAddReleaseNote.isLoading
                }
                title={ editReleaseNote.id === undefined ? "Create new release note" : "Edit release note"}
            >
                <Divider className={classes.divider} />
                <ReleaseNoteEditor
                    releaseNote={ editReleaseNote }
                    onSubmit={ handleOnSubmit }
                    resetButtonClickHandler={ resetButtonClickHandler }
                />
            </PagePaper>

            <Divider className={classes.divider} />
            <PagePaper 
                isLoading={existingReleaseNotesQuery.isLoading} 
                title={"Existing Release Notes"}
            >
                <Divider className={classes.divider} />
                { releaseNotes === undefined ? <h1>Loading...</h1> :
                    <ReleaseNotesDataGrid 
                        releaseNotes={releaseNotes}
                        editButtonClickHandler={ editReleaseNoteClickHandler }
                        deleteButtonClickHandler={ deleteReleaseNoteClickHandler }
                    />
                }
            </PagePaper>
        </div>
    );
}
